import React from 'react'
import { useTheme } from '~/hooks/use-theme'
import { type ScopedProps, useOptionMenuContext } from '../../option-menu'
import { isItemElement } from '../../utils/is-item-element'
import { Divider } from '../divider'
import { Group } from '../group'

type ListProps = {
  children: React.ReactNode
  maxHeight?: number | string
  testID?: string
}

const SCOPE_ERROR_MESSAGE =
  'Only Fragment, OptionMenu.Item and Group components are allowed as children of OptionMenu.List. Please remove or replace any other elements.'

export function List({ maxHeight = 480, ...props }: Readonly<ListProps>) {
  const { __scopeOptionMenu, children, testID } =
    props as ScopedProps<ListProps>
  const theme = useTheme()
  const { type } = useOptionMenuContext('OptionMenuList', __scopeOptionMenu)

  let childItems: React.ReactNode[] = []

  React.Children.forEach(children, child => {
    if (!React.isValidElement(child)) return

    if (child.type === React.Fragment) {
      const fragmentChildren = child.props.children as React.ReactNode
      childItems.push(fragmentChildren)
      return
    }

    if (
      isItemElement(child) ||
      child.type === Group ||
      child.type === Divider
    ) {
      childItems.push(child)
    } else {
      throw new Error(SCOPE_ERROR_MESSAGE)
    }
  })

  childItems = childItems.map(child => {
    if (isItemElement(child)) {
      return React.cloneElement(child, {
        key: (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1),
        type,
      })
    }
    return child
  })

  return (
    <div
      className={`flex max-h-[${maxHeight}px] flex-col overflow-y-scroll px-onepulse py-onepulse ${theme.spacingInlineOnepulse}`}
      data-testid={testID}
    >
      {childItems}
    </div>
  )
}
